import Combobox from "@/components/Combobox";
import FormHeader from "@/components/FormHeader";
import InputError from "@/components/InputError";
import Modal from "@/components/Modal";
import SegmentedInput from "@/components/SegmentedInput";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import { useDebounce } from "@/hooks/useDebounce";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import api from "@/lib/axios";
import { formatDateIndonesia } from "@/lib/utils";
import {
  getListJurusanSekolah,
  getListMapelKumer,
  getListSekolah,
} from "@/services/getData";
import {
  JurusanSekolah,
  MapelPeminatanKumer,
  Option,
  Periode,
  Sekolah,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ExternalLink, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { MdWarning } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { InputState, storeDataPendaftaran } from "../DashboardSiswaPage";

const verifikasiData = async (data: { nisn: string; no_utbk: string }) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/pendaftaran/check-peserta-utbk",
    data,
  );
  return response.data;
};

export default function FormSkorUTBK({
  selectedPeriode,
  onClose,
}: {
  selectedPeriode: Periode | null;

  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { dataPeriode } = useTimePeriode();
  const navigate = useNavigate();
  const [_, setSessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const [form, setForm] = useState<InputState>({
    id_sys_periode: selectedPeriode?.id_sys_periode || "",
    nama_lengkap: "",
    nisn: "",
    id_ref_sekolah: "",
    id_ref_jurusan_sekolah: "",
    is_kurikulum_merdeka: null,
    nomor_utbk: "",
    uk_id: "",
    asal_pt: "",
    asal_prodi: "",
    nim: "",
    tahun_ijazah: "",
    list_id_ref_mapel_peminatan_kurikulum_merdeka: [],
    tanggal_lahir: "",
    jenjang_pendidikan_terakhir_daftar_rpl: null,
  });

  const [isVerified, setIsVerified] = useState(false);
  const [showConfirmDaftar, setShowConfirmDaftar] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);

  const [openListJurusan, setOpenListJurusan] = useState(false);
  const [openListSekolah, setOpenListSekolah] = useState(false);
  const [searchQuerySekolah, setSearchQuerySekolah] = useState("");
  const [selectedSekolah, setSelectedSekolah] = useState<
    (Option & { bentuk_pendidikan: string }) | null
  >(null);
  const [selectedMapels, setSelectedMapels] = useState<string[]>([]);
  const debouncedSearchQuerySekolah = useDebounce(searchQuerySekolah, 1000);

  const { data: dataSekolah, isPending: isPendingSekolah } = useQuery({
    queryKey: [
      "listSekolah",
      debouncedSearchQuerySekolah,
      debouncedSearchQuerySekolah,
    ],
    queryFn: () => getListSekolah("1", "10", debouncedSearchQuerySekolah),
    select: (data) =>
      data?.data?.map((sekolah: Sekolah) => ({
        label: `${sekolah.nama_sekolah} (${sekolah.npsn_sekolah})`,
        value: `${sekolah.id_ref_sekolah}`,
        bentuk_pendidikan: sekolah.bentuk_pendidikan,
        npsn: sekolah.npsn_sekolah,
      })),
  });

  const isSMA =
    selectedSekolah &&
    (selectedSekolah?.bentuk_pendidikan?.startsWith("SMA") ||
      selectedSekolah?.bentuk_pendidikan?.startsWith("MA"));
  const isSMK =
    selectedSekolah && selectedSekolah?.bentuk_pendidikan?.startsWith("SMK");

  const { data: listJurusan, isPending: isPendingListJurusan } = useQuery({
    queryKey: ["listJurusan", selectedSekolah],
    queryFn: () => getListJurusanSekolah(selectedSekolah?.bentuk_pendidikan),
    select: (data) =>
      data?.map((jurusan: JurusanSekolah) => ({
        label: jurusan.nama_jurusan_sekolah,
        value: `${jurusan.id_ref_jurusan_sekolah}`,
      })),
    enabled: !!selectedSekolah,
  });

  const currentJurusan = listJurusan?.find((option: Option) => {
    return option.value == form.id_ref_jurusan_sekolah;
  });

  const filteredListJurusan = listJurusan?.filter((option: Option) => {
    return !option?.label?.startsWith("Umum");
  });

  const { data: listMapelPeminatan } = useQuery({
    queryKey: ["listMapelPeminatan"],
    queryFn: () => getListMapelKumer(),
    select: (data) =>
      data?.map((mapel: MapelPeminatanKumer) => ({
        label: `${mapel.nama_mapel}`,
        value: `${mapel?.idref_mapel_peminatan_kurikulum_merdeka}`,
      })),
  });

  const currentMapels =
    selectedMapels?.length > 0
      ? (listMapelPeminatan?.filter((option: Option) =>
          selectedMapels.includes(option.value),
        ) ?? [])
      : [];

  useEffect(() => {
    const currentSekolah = dataSekolah?.find((option: Option) => {
      return option.value == form.id_ref_sekolah;
    });

    setSelectedSekolah(currentSekolah);
  }, [dataSekolah]);

  const queryClient = useQueryClient();
  const {
    mutate,
    isPending: isPendingStorePendaftaran,
    error,
  } = useMutation({
    mutationFn: storeDataPendaftaran,
    onSuccess: (data) => {
      flushSync(() => {
        setSessionPeriode({ id_sys_periode: data?.data?.id_sys_periode });
      });

      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      queryClient.invalidateQueries({ queryKey: ["dataPendaftaran"] });
      queryClient.invalidateQueries({ queryKey: ["dataSiswa"] });
      navigate(data?.data?.redirect_url);
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      setShowConfirmDaftar(false);

      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const {
    mutate: verifyData,
    isPending: isPendingVerify,
    error: errorVerify,
  } = useMutation({
    mutationFn: verifikasiData,
    onSuccess: (data) => {
      setForm((prev) => ({
        ...prev,
        nama_lengkap: data?.data?.nama,
        tanggal_lahir: data?.data?.tgl_lahir,
        tahun_ijazah: data?.data?.tahun_lulus?.toString().substring(0, 4),
        id_ref_sekolah: data?.data?.id_ref_sekolah,
      }));
      setSearchQuerySekolah(data?.data?.npsn);
      toast.success("Data siswa berhasil diverifikasi!");
      setIsVerified(true);
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const errorMessage =
        error.response?.data?.message ||
        "Data tidak ditemukan atau terjadi kesalahan.";
      toast.error(errorMessage);
    },
  });

  const handleVerifikasi = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.nisn || !form.nomor_utbk) {
      toast.error("NISN dan Nomor UTBK wajib diisi!");
      return;
    }

    verifyData({
      nisn: form.nisn,
      no_utbk: form.nomor_utbk || "",
    });
  };

  const handleSubmitPendaftaran = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    mutate({
      ...form,
      list_id_ref_mapel_peminatan_kurikulum_merdeka: selectedMapels,
    });
  };

  if (isVerified) {
    return (
      <div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setShowConfirmDaftar(true);
            setIsConfirm(false);
          }}
          className="flex flex-col gap-y-3 dark:text-slate-200"
        >
          <h1 className="mb-3 font-bold text-xl text-center text-slate-900 dark:text-white">
            {t("siswa.layout.register.title")}{" "}
            <span className="text-primary dark:text-blue-400">
              {selectedPeriode?.jalur_jenis?.nama_jalur_jenis}
            </span>
          </h1>
          <div>
            <Label required className="dark:text-slate-200">
              {t("siswa.layout.register.label.nama-lengkap")}
            </Label>
            <Input
              disabled
              required
              placeholder={`type ${t(
                "siswa.layout.register.label.nama-lengkap",
              )}...`}
              value={form.nama_lengkap}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  nama_lengkap: event.target.value,
                }))
              }
              className="dark:bg-slate-950 dark:border-slate-800 dark:text-white dark:placeholder:text-slate-500"
            />
            <p className="text-muted-foreground dark:text-slate-400 text-sm">
              {t("siswa.layout.register.label.nama-lengkap.description")}
            </p>
            <InputError
              text={error?.response?.data.errors?.nama_lengkap?.join(",")}
            />
          </div>
          <div>
            <Label>Nomor UTBK</Label>
            <Input disabled value={form.nomor_utbk} />
          </div>
          <div>
            <Label
              className="dark:text-slate-200"
              required={
                selectedPeriode?.jalur_jenis?.nama_jalur_jenis_slug ==
                "pascasarjana"
                  ? false
                  : true
              }
            >
              NISN
            </Label>
            <SegmentedInput
              disabled
              type="number"
              length={10}
              defaultValue={form.nisn}
              onComplete={(value: string) => {
                setForm((prev) => ({
                  ...prev,
                  nisn: value,
                }));
              }}
            />
            <InputError text={error?.response?.data.errors?.nisn?.join(",")} />
            <div>
              <p className="text-muted-foreground dark:text-slate-400">
                NISN bisa anda lihat melalui link berikut
              </p>
              <Link
                to="https://nisn.data.kemendikdasmen.go.id"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline transition-colors"
              >
                Cek NISN disini
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          <div>
            <Label required className="dark:text-slate-200">
              Tanggal Lahir
            </Label>
            <Input
              readOnly
              required
              type="date"
              value={form.tanggal_lahir || "2001"}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  tanggal_lahir: event.target.value,
                }))
              }
              className="dark:bg-slate-950 dark:border-slate-800 dark:text-white dark:[color-scheme:dark]"
            />
            <p className="text-primary dark:text-blue-400 font-semibold">
              {form.tanggal_lahir &&
                formatDateIndonesia(form.tanggal_lahir, "medium")}
            </p>
            <InputError
              text={error?.response?.data?.errors?.tanggal_lahir?.join(",")}
            />
          </div>
          <div>
            <Label required className="dark:text-slate-200">
              Sekolah Asal
            </Label>
            <Combobox
              serverSide
              open={openListSekolah}
              setOpen={setOpenListSekolah}
              value={form.id_ref_sekolah || ""}
              setValue={(newValue) => {
                const selected = dataSekolah?.find((option: Option) => {
                  return String(option.value) == String(newValue);
                });
                setSelectedSekolah(selected);
                setForm((prev) => ({
                  ...prev,
                  id_ref_sekolah: newValue,
                  id_ref_jurusan_sekolah: "",
                }));
              }}
              onChange={(event) => setSearchQuerySekolah(event.target.value)}
              options={dataSekolah}
              placeholder="Pilih Sekolah"
              isPending={isPendingSekolah}
              searchQueryValue={searchQuerySekolah || ""}
            />
            {/* <p className="text-muted-foreground dark:text-slate-400 font-semibold">
              Cari berdasarkan nama sekolah atau NPSN Sekolah!
            </p> */}
            <InputError
              text={error?.response?.data.errors?.id_ref_sekolah?.join(",")}
            />
          </div>

          <div>
            <Label required className="dark:text-slate-200">
              Kurikulum yang digunakan?
            </Label>
            <div className="mt-2 flex gap-7">
              <div className="flex gap-3 items-center">
                <Checkbox
                  required={form.is_kurikulum_merdeka === null}
                  checked={form.is_kurikulum_merdeka === false}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setForm((prev) => ({
                        ...prev,
                        is_kurikulum_merdeka: false,
                      }));
                    } else {
                      setForm((prev) => ({
                        ...prev,
                        is_kurikulum_merdeka: null,
                      }));
                    }
                  }}
                  className="dark:border-slate-600"
                />
                <Label className="dark:text-slate-300">Kurikulum 2013</Label>
              </div>
              <div className="flex gap-3 items-center">
                <Checkbox
                  required={form.is_kurikulum_merdeka === null}
                  checked={form.is_kurikulum_merdeka === true}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setForm((prev) => ({
                        ...prev,
                        is_kurikulum_merdeka: true,
                      }));
                    } else {
                      setForm((prev) => ({
                        ...prev,
                        is_kurikulum_merdeka: null,
                      }));
                    }
                  }}
                  className="dark:border-slate-600"
                />
                <Label className="dark:text-slate-300">Kurikulum Merdeka</Label>
              </div>
              {!isSMK && (
                <div className="flex gap-3 items-center">
                  <Checkbox
                    required={form.is_kurikulum_merdeka === null}
                    checked={form.is_kurikulum_merdeka == 2}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setForm((prev) => ({
                          ...prev,
                          is_kurikulum_merdeka: 2,
                        }));
                      } else {
                        setForm((prev) => ({
                          ...prev,
                          is_kurikulum_merdeka: null,
                        }));
                      }
                    }}
                    className="dark:border-slate-600"
                  />
                  <Label className="dark:text-slate-300">Lainnya</Label>
                </div>
              )}
            </div>
          </div>
          {form.is_kurikulum_merdeka !== null &&
            (isSMK ||
              (isSMA && form.is_kurikulum_merdeka == false) ||
              (!isSMA && !isSMK && form.is_kurikulum_merdeka == false)) && (
              <div>
                <Label required className="dark:text-slate-200">
                  Jurusan/Program Keahlian Sekolah
                </Label>
                <Combobox
                  isPending={isPendingListJurusan}
                  open={openListJurusan}
                  setOpen={setOpenListJurusan}
                  value={form.id_ref_jurusan_sekolah || ""}
                  setValue={(value) => {
                    setForm((prev) => ({
                      ...prev,
                      id_ref_jurusan_sekolah: value,
                    }));
                    setSelectedMapels([]);
                  }}
                  options={filteredListJurusan}
                  placeholder="Pilih jurusan sekolah"
                />
                <InputError
                  text={error?.response?.data.errors?.id_ref_jurusan_sekolah?.join(
                    ",",
                  )}
                />
              </div>
            )}

          {(isSMA && form.is_kurikulum_merdeka) ||
          (!isSMA && !isSMK && form.is_kurikulum_merdeka) ||
          (!isSMA && !isSMK && form.is_kurikulum_merdeka === 2) ? (
            <div>
              <Label required className="dark:text-slate-200">
                Mapel Peminatan Kurikulum Merdeka (Minimal 4, Maksimal 5)
              </Label>
              <MultiSelect
                options={listMapelPeminatan || []}
                onValueChange={(value) => {
                  setSelectedMapels(value);
                  setForm((prev) => ({
                    ...prev,
                    id_ref_jurusan_sekolah: "",
                  }));
                }}
                defaultValue={selectedMapels}
                placeholder="Pilih Mapel Peminatan"
                variant="inverted"
                animation={2}
                maxCount={3}
                className="dark:bg-slate-950 dark:border-slate-800 dark:text-white"
              />
              <InputError
                text={error?.response?.data.errors?.list_id_ref_mapel_peminatan_kurikulum_merdeka?.join(
                  ",",
                )}
              />
            </div>
          ) : (
            ""
          )}

          <div>
            <Label required className="dark:text-slate-200">
              {t("siswa.layout.register.label.tahun-kelulusan")}
            </Label>
            <Input
              required
              type="number"
              maxLength={4}
              placeholder={`type ${t(
                "siswa.layout.register.label.tahun-kelulusan",
              )}...`}
              value={form.tahun_ijazah}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  tahun_ijazah: event.target.value,
                }))
              }
              className="[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none dark:bg-slate-950 dark:border-slate-800 dark:text-white dark:placeholder:text-slate-500"
              disabled
            />
            <InputError
              text={error?.response?.data.errors?.tahun_ijazah?.join(",")}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800 mt-2">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => onClose()}
            >
              {t("siswa.layout.register.button.batal")}
            </Button>

            <Button
              type="submit"
              variant={"default"}
              className="dark:bg-slate-50 dark:text-slate-900 dark:hover:bg-slate-200"
            >
              {t("siswa.layout.register.button.daftar")}
            </Button>
          </div>
        </form>

        <Modal
          className="w-[95vw] max-w-2xl"
          show={showConfirmDaftar}
          onClose={() => {
            setShowConfirmDaftar(false);
            setIsConfirm(false);
          }}
        >
          <div className="p-1 sm:p-2">
            <FormHeader
              title={`${t("siswa.layout.register.confirmation.title")} ${
                selectedPeriode?.jalur_jenis?.nama_jalur_jenis
              }`}
              icon={<MdWarning className="text-amber-500 w-6 h-6" />}
            />

            <form
              onSubmit={handleSubmitPendaftaran}
              className="mt-6 flex flex-col gap-6"
            >
              {/* KONDISI: Jalur Mandiri SMBJM */}
              <div className="flex flex-col gap-6">
                {/* Box Detail Data Peserta */}
                <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-5 border border-slate-200 dark:border-slate-800">
                  <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-4 uppercase tracking-wider border-b border-slate-200 dark:border-slate-800 pb-2">
                    Verifikasi Data Anda
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold text-slate-500 uppercase">
                        Nama Lengkap
                      </span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {form.nama_lengkap}
                      </span>
                    </div>

                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold text-slate-500 uppercase">
                        NISN
                      </span>
                      <span className="text-sm font-mono font-medium text-slate-900 dark:text-slate-100">
                        {form.nisn}
                      </span>
                    </div>

                    <div className="flex flex-col gap-1 sm:col-span-2">
                      <span className="text-xs font-semibold text-slate-500 uppercase">
                        Asal Sekolah
                      </span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {selectedSekolah?.label}
                      </span>
                    </div>

                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold text-slate-500 uppercase">
                        Tahun Kelulusan
                      </span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {form.tahun_ijazah}
                      </span>
                    </div>

                    {form.is_kurikulum_merdeka && isSMA && (
                      <div className="flex flex-col gap-1 sm:col-span-2">
                        <span className="text-xs font-semibold text-slate-500 uppercase">
                          Mapel Peminatan
                        </span>
                        {currentMapels && currentMapels?.length > 0 ? (
                          <div className="flex flex-wrap gap-2 mt-1">
                            {currentMapels?.map(
                              (option: Option, idx: number) => (
                                <Badge
                                  key={idx}
                                  variant="secondary"
                                  className="font-medium bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                                >
                                  {option.label}
                                </Badge>
                              ),
                            )}
                          </div>
                        ) : (
                          <span className="text-sm font-medium text-red-500 flex items-center gap-1 mt-1">
                            <MdWarning /> Mapel Peminatan belum dipilih!
                          </span>
                        )}
                      </div>
                    )}

                    {(isSMK || !form.is_kurikulum_merdeka) && (
                      <div className="flex flex-col gap-1 sm:col-span-2">
                        <span className="text-xs font-semibold text-slate-500 uppercase">
                          Jurusan / Program Keahlian
                        </span>
                        {currentJurusan ? (
                          <span className="text-sm font-medium text-slate-900 dark:text-slate-100 mt-1">
                            {currentJurusan?.label}
                          </span>
                        ) : (
                          <span className="text-sm font-medium text-red-500 flex items-center gap-1 mt-1">
                            <MdWarning /> Jurusan Sekolah Asal belum dipilih!
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Box Konfirmasi (Checkbox) */}
                <div
                  className={`p-4 sm:p-5 rounded-xl border transition-colors duration-200 ${isConfirm ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-900/50" : "bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-900/50"}`}
                >
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div className="mt-0.5">
                      <Checkbox
                        checked={isConfirm}
                        onCheckedChange={(checked) =>
                          setIsConfirm(checked ? true : false)
                        }
                        className={`data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600 ${!isConfirm && "border-amber-500"}`}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-slate-100">
                        Pernyataan Persetujuan
                      </span>
                      <span
                        className={`text-sm leading-relaxed transition-colors ${isConfirm ? "text-emerald-800 dark:text-emerald-300" : "text-amber-800 dark:text-amber-400"}`}
                      >
                        Dengan ini saya menyatakan NISN, Nama, Tahun Kelulusan,
                        Sekolah, Jurusan/Program Keahlian Sekolah{" "}
                        <strong>sudah benar</strong>, saya dinyatakan{" "}
                        <strong>TIDAK LULUS</strong> pada jalur SNBP tahun{" "}
                        {selectedPeriode?.tahun}, serta tidak{" "}
                        <strong>MENDAFTAR KEMBALI</strong> pada jalur SNBT tahun{" "}
                        {selectedPeriode?.tahun}.
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full sm:w-auto"
                  onClick={() => {
                    setShowConfirmDaftar(false);
                    setIsConfirm(false);
                  }}
                  disabled={isPendingStorePendaftaran}
                >
                  {t("siswa.layout.register.button.batal")}
                </Button>

                <Button
                  type="submit"
                  className="w-full sm:w-auto min-w-[140px]"
                  disabled={isPendingStorePendaftaran || !isConfirm}
                  variant="default"
                >
                  {isPendingStorePendaftaran ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    t("siswa.layout.register.confirmation.button")
                  )}
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleVerifikasi}
      className="max-w-[36rem] flex flex-col gap-6"
    >
      <h1 className="mb-3 font-bold text-xl text-center text-slate-900 dark:text-white">
        {t("siswa.layout.register.title")}{" "}
        <span className="text-primary dark:text-blue-400">
          {selectedPeriode?.jalur_jenis?.nama_jalur_jenis}
        </span>
      </h1>
      {/* --- AREA FORM --- */}
      <div className="space-y-5">
        {/* Form Group: NISN */}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="nisn" className="text-sm font-semibold" required>
            NISN
          </Label>
          {/* <Input
            id="nisn"
            className="h-11 transition-all focus:ring-2 focus:ring-primary/20"
            minLength={10}
            maxLength={10}
            required
            placeholder="Contoh: 0051234567"
            value={form.nisn}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, nisn: event.target.value }))
            }
          /> */}
          <SegmentedInput
            key="nisn"
            type="number"
            length={10}
            onComplete={(value: string) => {
              setForm((prev) => ({ ...prev, nisn: value }));
            }}
          />
          <div className="flex justify-between items-start">
            <p className="text-xs text-muted-foreground">
              Masukkan 10 digit NISN yang valid.
            </p>
            <InputError text={error?.response?.data.errors?.nisn?.join(",")} />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="noutbk" className="text-sm font-semibold" required>
            Nomor UTBK SNBT {dataPeriode?.tahun}
          </Label>
          {/* <Input
            id="noutbk"
            className="h-11 transition-all focus:ring-2 focus:ring-primary/20"
            minLength={12}
            maxLength={12}
            required
            placeholder="Contoh: 005123456789"
            value={form.nomor_utbk}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, nomor_utbk: event.target.value }))
            }
          /> */}
          <SegmentedInput
            key="noutbk"
            type="number"
            length={12}
            onComplete={(value: string) => {
              setForm((prev) => ({ ...prev, nomor_utbk: value }));
            }}
          />
          <div className="flex justify-between items-start">
            <p className="text-xs text-muted-foreground">
              Masukkan 12 digit Nomor UTBK yang valid.
            </p>
            <InputError
              text={error?.response?.data.errors?.no_utbk?.join(",")}
            />
          </div>
        </div>
      </div>

      {errorVerify?.response?.data?.message && (
        <div className="text-red-500 text-sm font-semibold bg-red-50 dark:bg-red-950/20 p-2 rounded-md border border-red-100 dark:border-red-900 text-center">
          {errorVerify.response.data.message}
        </div>
      )}

      {/* --- AREA TOMBOL (ACTIONS) --- */}
      <div className="flex items-center justify-end gap-3 pt-4 mt-2 border-t border-slate-100 dark:border-slate-800">
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          disabled={isPendingVerify}
        >
          Batal
        </Button>
        <Button type="submit" disabled={isPendingVerify}>
          {isPendingVerify ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            "Cek Data & Lanjutkan"
          )}
        </Button>
      </div>
    </form>
  );
}
